"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, ArrowUp, Trash2 } from "lucide-react";
import DOMPurify from "dompurify";
import TextareaAutosize from "react-textarea-autosize";

import { useChat } from "@/hooks/useChatAdapter";
import { useSabineChatPersistence } from "@/hooks/useSabineChatPersistence";
import { useSubmit } from "@/hooks/useSubmit";
import { cleanAndHighlightMarkdown } from "@/lib/utils";
import {
  SABINE_DIRECT_AVATAR_SRC,
  SABINE_SUBTITLE,
  SABINE_CORAL_GRADIENT,
} from "@/lib/sabine";
import AgentActionLinks from "@/components/agent-actions/AgentActionLinks";
import SabineMessageActions from "@/components/home/SabineMessageActions";
import VoiceCommentInput from "@/components/voice/VoiceCommentInput";
import { AssemblyContextProvider } from "@/context/SimpleVoiceInput";
import { MicrophoneContextProvider } from "@/context/MicrophoneAssembly";
import { Kbd } from "@/components/ui/kbd";

const STORAGE_PREFIX = "salesably.home.sabine.";

// Render Sabine's markdown to sanitized HTML, forcing inline links to open in
// a new tab (and not leak the opener) so a click never navigates the user away
// from the chat thread. The negative lookahead leaves any anchor that already
// declares a target untouched.
function renderSabineMarkdown(content = "") {
  const html = DOMPurify.sanitize(cleanAndHighlightMarkdown(content || ""), {
    ADD_ATTR: ["target", "rel"],
  });
  return html.replace(
    /<a (?![^>]*\btarget=)/g,
    '<a target="_blank" rel="noopener noreferrer" '
  );
}

function readStoredMessages(key) {
  if (typeof window === "undefined") return [];
  try {
    const saved = window.localStorage.getItem(key);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-2">
      <span className="thinking-dot h-1.5 w-1.5 rounded-full bg-brand-500" />
      <span className="thinking-dot h-1.5 w-1.5 rounded-full bg-brand-500 [animation-delay:0.2s]" />
      <span className="thinking-dot h-1.5 w-1.5 rounded-full bg-brand-500 [animation-delay:0.4s]" />
    </div>
  );
}

/**
 * Sabine — the home page "ask anything" assistant. An inviting hero composer in
 * the empty state, then a real chat card (scrolling thread + bottom-pinned
 * input) once a conversation starts. Streams from /api/home/sabine, grounded in
 * the playbook, recent practice, learning paths, and due work. Conversation
 * persists per user and studio, so switching studios never shows a thread
 * about another studio's team.
 */
const DEFAULT_PLACEHOLDER =
  "Ask Sabine anything — what to practice, what's due, or anything in the playbook…";

export default function SabineHero(props) {
  // Dictation needs the same two voice contexts every other mic button mounts
  // (ResultCoachDock, SupportDialog); the home page has no provider above it.
  return (
    <MicrophoneContextProvider>
      <AssemblyContextProvider>
        <SabineChat {...props} />
      </AssemblyContextProvider>
    </MicrophoneContextProvider>
  );
}

function SabineChat({
  userId,
  workspaceId,
  prompts = [],
  placeholder = DEFAULT_PLACEHOLDER,
}) {
  const storageKey = `${STORAGE_PREFIX}${userId || "anon"}.${workspaceId || "none"}`;

  const {
    messages,
    input,
    handleInputChange,
    isLoading,
    sendMessage,
    setMessages,
  } = useChat({
    api: "/api/home/sabine",
    initialMessages: [],
  });

  const { resetAndClear: resetAndClearSavedChat } = useSabineChatPersistence({
    source: "HOME_SABINE",
    contextId: workspaceId,
    messages,
    setMessages,
    enabled: Boolean(userId && workspaceId),
  });

  const scrollRef = useRef(null);
  // Hydrate the saved thread after mount (never during render) so the server
  // and first client render match. Guarded by a ref so it runs exactly once
  // per user key: the chat adapter returns a fresh setMessages on every render,
  // so a dependency-driven re-run would call setMessages in a loop (React #185).
  const hydratedKeyRef = useRef(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (hydratedKeyRef.current === storageKey) return;
    hydratedKeyRef.current = storageKey;
    const stored = readStoredMessages(storageKey);
    if (stored.length) setMessages(stored);
    setHydrated(true);
  }, [storageKey, setMessages]);

  useEffect(() => {
    if (!hydrated || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(messages));
    } catch (error) {
      /* ignore quota / serialization errors */
    }
  }, [messages, storageKey, hydrated]);

  // Keep the thread pinned to the newest content. Keyed on message count + the
  // last message's length so it also follows a streaming reply, without
  // depending on the (churning) messages array identity.
  const lastMessage = messages[messages.length - 1];
  const scrollSignal = `${messages.length}:${lastMessage?.content?.length || 0}`;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [scrollSignal, isLoading]);

  const sendUserMessage = useCallback(
    async (value) => {
      try {
        await sendMessage({ role: "user", content: value });
      } catch (error) {
        console.error("Failed to send Sabine message", error);
      }
    },
    [sendMessage]
  );

  const ask = useCallback(
    (text) => {
      const value = (text ?? "").trim();
      if (!value || isLoading) return;
      void sendUserMessage(value);
    },
    [isLoading, sendUserMessage]
  );

  const onSubmit = useCallback(
    (event) => {
      if (event?.preventDefault) event.preventDefault();
      const value = input.trim();
      if (!value || isLoading) return;
      handleInputChange({ target: { value: "" } });
      void sendUserMessage(value);
    },
    [input, isLoading, handleInputChange, sendUserMessage]
  );

  const { onKeyDown } = useSubmit();

  const started = messages.length > 0;

  const clear = useCallback(() => {
    void resetAndClearSavedChat();
    if (typeof window !== "undefined") {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        /* ignore */
      }
    }
  }, [resetAndClearSavedChat, storageKey]);

  // Dictated text joins whatever is already typed, as in the Ask Studio chat.
  const appendDictation = useCallback(
    (text) => {
      const value = `${input}${input ? " " : ""}${text}`.trim();
      handleInputChange({ target: { value } });
    },
    [input, handleInputChange]
  );

  const canSend = Boolean(input.trim()) && !isLoading;
  const sendButton = (
    <button
      type="submit"
      disabled={!canSend}
      aria-label="Send message"
      style={canSend ? { backgroundImage: SABINE_CORAL_GRADIENT } : undefined}
      className="group/send relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl text-white shadow-sm transition-all duration-200 hover:-translate-y-px disabled:cursor-default disabled:translate-y-0 disabled:bg-gray-100 disabled:text-gray-400 disabled:shadow-none"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/40 to-transparent transition-transform duration-500 group-hover/send:translate-x-full"
      />
      <ArrowUp className="relative h-4 w-4" />
    </button>
  );
  const composerActions = (
    <div className="flex shrink-0 items-center gap-2">
      <VoiceCommentInput
        compact
        disabled={isLoading}
        onTranscriptionComplete={appendDictation}
      />
      {sendButton}
    </div>
  );

  // ---- Empty state: inviting hero composer + suggested prompts ----
  if (!started) {
    return (
      <section className="relative isolate mb-10" aria-label="Ask Sabine">
        {/* Ambient coral aurora — sets Sabine apart as the centerpiece. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-x-8 -top-12 bottom-0 -z-10 overflow-hidden"
        >
          <div className="animate-aurora absolute left-[14%] top-0 h-40 w-40 rounded-full bg-brand-400/25 blur-3xl" />
          <div className="animate-aurora absolute right-[12%] top-8 h-44 w-44 rounded-full bg-brand-300/20 blur-3xl [animation-delay:-7s]" />
        </div>

        <div className="mb-3 flex items-center gap-2.5">
          <span className="relative inline-flex">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={SABINE_DIRECT_AVATAR_SRC}
              alt=""
              className="h-9 w-9 rounded-full object-cover object-top ring-2 ring-white"
            />
            <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />
          </span>
          <div className="leading-tight">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
              Sabine
              <Sparkles className="h-3.5 w-3.5 text-brand-600" />
            </div>
            <div className="text-xs text-gray-500">{SABINE_SUBTITLE}</div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_1px_3px_rgba(17,24,39,0.04)] transition-all duration-200 focus-within:border-brand-500/50 focus-within:shadow-[0_0_0_4px_var(--sal-focus-glow)]"
        >
          <TextareaAutosize
            value={input}
            minRows={2}
            maxRows={6}
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="w-full resize-none overflow-hidden border-0 bg-transparent text-[15px] leading-6 text-gray-900 outline-none placeholder:text-gray-400"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              Press{" "}
              <Kbd>Enter</Kbd>{" "}
              to send
            </span>
            {composerActions}
          </div>
        </form>

        {prompts.length > 0 && (
          <div className="mt-3.5 flex flex-wrap gap-2">
            {prompts.map((prompt, index) => (
              <button
                key={prompt}
                type="button"
                onClick={() => ask(prompt)}
                style={{ "--rise-delay": `${200 + index * 55}ms` }}
                className="animate-rise group inline-flex items-center gap-1.5 rounded-full border border-gray-100 bg-white px-3.5 py-1.5 text-[13px] font-medium text-gray-700 shadow-[0_1px_2px_rgba(17,24,39,0.03)] transition-all duration-200 hover:-translate-y-px hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
              >
                <Sparkles className="h-3 w-3 text-gray-300 transition-colors group-hover:text-brand-600" />
                {prompt}
              </button>
            ))}
          </div>
        )}
      </section>
    );
  }

  // ---- Active state: card-less chat thread that blends with the page ----
  return (
    <section className="mb-10" aria-label="Ask Sabine">
      <div className="animate-pop-in flex max-h-[min(70vh,640px)] flex-col">
        <header className="flex items-center justify-between gap-3 border-b border-gray-200/70 pb-3">
          <span className="flex items-center gap-2.5 text-sm font-semibold text-gray-900">
            <span className="relative inline-flex">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={SABINE_DIRECT_AVATAR_SRC}
                alt=""
                className="h-7 w-7 rounded-full object-cover object-top ring-2 ring-white"
              />
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border-2 border-white bg-emerald-500" />
            </span>
            Sabine
          </span>
          <button
            type="button"
            onClick={clear}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Clear chat
          </button>
        </header>

        <div
          ref={scrollRef}
          className="thin-scrollbar min-h-0 flex-1 overflow-y-auto py-5"
        >
          <div className="flex flex-col gap-4">
            {messages.map((message, index) =>
              message.role === "user" ? (
                <div
                  key={index}
                  className="animate-message-in max-w-[80%] self-end rounded-2xl rounded-br-md bg-action-600 px-4 py-2.5 text-sm leading-relaxed text-white shadow-sm"
                >
                  {message.content}
                </div>
              ) : (
                <div
                  key={index}
                  className="animate-message-in flex max-w-[94%] items-start gap-3"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={SABINE_DIRECT_AVATAR_SRC}
                    alt=""
                    className="mt-0.5 h-7 w-7 shrink-0 rounded-full object-cover object-top ring-2 ring-white"
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className="prose prose-sm max-w-none text-sm leading-relaxed text-gray-800"
                      dangerouslySetInnerHTML={{
                        __html: renderSabineMarkdown(message.content || ""),
                      }}
                    />
                    <AgentActionLinks content={message.content || ""} className="mt-3" />
                    <SabineMessageActions
                      messages={messages}
                      index={index}
                      deferred={isLoading && index === messages.length - 1}
                    />
                  </div>
                </div>
              )
            )}
            {isLoading && lastMessage?.role === "user" && (
              <div className="animate-message-in flex items-start gap-3">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={SABINE_DIRECT_AVATAR_SRC}
                  alt=""
                  className="h-7 w-7 shrink-0 rounded-full object-cover object-top ring-2 ring-white"
                />
                <TypingDots />
              </div>
            )}
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-2 flex items-end gap-2 rounded-2xl border border-gray-200 bg-white p-2.5 shadow-[0_1px_3px_rgba(17,24,39,0.04)] transition-all focus-within:border-brand-500/50 focus-within:shadow-[0_0_0_4px_var(--sal-focus-glow)]"
        >
          <TextareaAutosize
            value={input}
            minRows={1}
            maxRows={6}
            autoFocus
            onChange={handleInputChange}
            onKeyDown={onKeyDown}
            placeholder="Reply to Sabine…"
            className="flex-1 resize-none overflow-hidden border-0 bg-transparent px-2 py-1.5 text-[15px] leading-6 text-gray-900 outline-none placeholder:text-gray-400"
          />
          {composerActions}
        </form>
      </div>
    </section>
  );
}
